import { flattenLeafCategories, getCategoryRoots, isLeafCategoryNode } from '@/shein-platform/utils/sheinCategoryTree';

/** @typedef {{ leafCategoryIds: Set<string>, leafProductTypeIds: Set<string> }} SheinShopCategoryScope */

/**
 * 从店铺类目树提取叶子 categoryId / productTypeId 作用域。
 * @param {unknown} categoriesRaw
 * @returns {SheinShopCategoryScope}
 */
export function collectShopCategoryScope(categoriesRaw) {
  const leafCategoryIds = new Set();
  const leafProductTypeIds = new Set();
  flattenLeafCategories(categoriesRaw).forEach((leaf) => {
    const categoryId = String(leaf.categoryId ?? '').trim();
    const productTypeId = String(leaf.productTypeId ?? '').trim();
    if (categoryId) leafCategoryIds.add(categoryId);
    if (productTypeId && productTypeId !== '0') leafProductTypeIds.add(productTypeId);
  });
  return { leafCategoryIds, leafProductTypeIds };
}

/**
 * 官方模板是否对该店类目可见（与后端 SheinShopOfficialTemplateVisibility 一致）。
 * @param {SheinShopCategoryScope|null|undefined} scope
 * @param {{ categoryId?: string|number, productTypeId?: string|number }} template
 */
export function shopSupportsOfficialTemplate(scope, template) {
  if (!scope) return false;
  const categoryId = String(template?.categoryId ?? '').trim();
  const productTypeId = String(template?.productTypeId ?? '').trim();
  const hasCategory = categoryId && scope.leafCategoryIds.has(categoryId);
  const hasType = productTypeId && productTypeId !== '0' && scope.leafProductTypeIds.has(productTypeId);
  return hasCategory || hasType;
}

/**
 * 筛选拥有指定官方模板类目的店铺。
 * @param {Array<{ id?: string|number }>} shops
 * @param {Map<string, SheinShopCategoryScope>} scopeByShopId
 * @param {{ categoryId?: string|number, productTypeId?: string|number }} template
 */
export function filterShopsForOfficialTemplate(shops, scopeByShopId, template) {
  if (!Array.isArray(shops) || !shops.length) return [];
  return shops.filter((shop) => {
    const sid = String(shop?.id ?? '').trim();
    if (!sid) return false;
    return shopSupportsOfficialTemplate(scopeByShopId.get(sid), template);
  });
}

/**
 * 批量复制：仅保留对所有模板类目均可见的店铺。
 */
export function filterShopsForOfficialTemplates(shops, scopeByShopId, templates) {
  const rows = Array.isArray(templates) ? templates.filter(Boolean) : [];
  if (!rows.length) return Array.isArray(shops) ? shops : [];
  return (Array.isArray(shops) ? shops : []).filter((shop) => {
    const sid = String(shop?.id ?? '').trim();
    if (!sid) return false;
    const scope = scopeByShopId.get(sid);
    return rows.every((row) => shopSupportsOfficialTemplate(scope, row));
  });
}

function mergeCategoryNodeLists(targetList, sourceNode) {
  if (!sourceNode || typeof sourceNode !== 'object') return;
  const id = String(sourceNode.category_id ?? '').trim();
  if (!id) return;
  let hit = targetList.find((node) => String(node.category_id ?? '') === id);
  if (!hit) {
    hit = { ...sourceNode, children: [] };
    targetList.push(hit);
  } else if (sourceNode.category_name && !hit.category_name) {
    hit.category_name = sourceNode.category_name;
  }
  const srcChildren = Array.isArray(sourceNode.children) ? sourceNode.children : [];
  if (!srcChildren.length) {
    hit.last_category = hit.last_category ?? sourceNode.last_category ?? isLeafCategoryNode(sourceNode);
    if (sourceNode.product_type_id != null) hit.product_type_id = sourceNode.product_type_id;
    hit.children = [];
    return;
  }
  if (!Array.isArray(hit.children)) hit.children = [];
  srcChildren.forEach((child) => mergeCategoryNodeLists(hit.children, child));
}

/**
 * 合并多店类目树（官方「全部」筛选器用）。
 * @param {unknown[]} categoryTrees
 */
export function mergeShopCategoryTrees(categoryTrees) {
  const merged = [];
  (Array.isArray(categoryTrees) ? categoryTrees : []).forEach((raw) => {
    getCategoryRoots(raw).forEach((root) => mergeCategoryNodeLists(merged, root));
  });
  return merged;
}
